import Image, { ImageProps } from 'next/image';

import { LogoName } from '@sola/schema/lib/logos.js';

import { cn } from '../../utils/helpers.js';

const logoColors = {
  blue: 'blue',
  black: 'black',
  white: 'white',
  full: 'full',
  blueLegacy: 'blueLegacy',
  gray: 'gray',
} as const;
type LogoColors = (typeof logoColors)[keyof typeof logoColors];

export type LogoChooserProps = Omit<ImageProps, 'src' | 'alt' | 'color'> & {
  name: LogoName;
  alt?: string;
  color?: LogoColors;
  priority?: boolean;
};

type LogoImgData = {
  base: string;
  width: number;
  height: number;
};

const logoSVG: Record<LogoName, LogoImgData> = {
  compassion: { base: 'LogoCompassion', width: 126, height: 48 },
  'apple-pay': { base: 'LogoApplePay', width: 55, height: 23 },
  'compassion-explorer': {
    base: 'LogoCompassionExplorer',
    width: 75,
    height: 24,
  },
  'compassion-journey': {
    base: 'LogoCompassionJourney',
    width: 76,
    height: 18,
  },
  'compassion-moments': {
    base: 'LogoCompassionMoments',
    width: 75,
    height: 19,
  },
  'compassion-simple': {
    base: 'LogoCompassionSimple',
    width: 148,
    height: 59,
  },
  'compassion-sunday': {
    base: 'LogoCompassionSunday',
    width: 76,
    height: 22,
  },
  'compassion-experience': {
    base: 'LogoCompassionExperience',
    width: 145,
    height: 56,
  },
  skippy: { base: 'LogoSkippy', width: 48, height: 48 },
  'trust-charity-watch': {
    base: 'LogoTrustCharityWatch',
    width: 53,
    height: 40,
  },
  'trust-ecfa': { base: 'LogoTrustECFA', width: 40, height: 40 },
  'trust-bbb': { base: 'LogoTrustBBB', width: 27, height: 40 },
  'trust-guide-star': { base: 'LogoTrustGuideStar', width: 41, height: 40 },
  'trust-charity-navigator': {
    base: 'LogoTrustCharityNavigator',
    width: 78,
    height: 25,
  },
  'trust-norton': { base: 'LogoTrustNorton', width: 58, height: 32 },
  'trust-seal-of-transparency-guide-star': {
    base: 'LogoTrustSealOfTransparencyGuideStar',
    width: 40,
    height: 40,
  },
};

const logoSVGColor = {
  'compassion--blue': `${logoSVG.compassion.base}--blue`,
  'compassion--white': `${logoSVG.compassion.base}--white`,
  'compassion--blueLegacy': `${logoSVG.compassion.base}--blueLegacy`,
  'compassion--gray': `${logoSVG.compassion.base}--gray`,
  'compassion-simple--blue': `${logoSVG['compassion-simple'].base}--blue`,
  'compassion-simple--white': `${logoSVG['compassion-simple'].base}--white`,
  'compassion-simple--blueLegacy': `${logoSVG['compassion-simple'].base}--blueLegacy`,
  'compassion-simple--gray': `${logoSVG['compassion-simple'].base}--gray`,
  'skippy--white': `${logoSVG.skippy.base}--white`,
  'skippy--blue': `${logoSVG.skippy.base}--blue`,
  'skippy--blueLegacy': `${logoSVG.skippy.base}--blueLegacy`,
  'skippy--gray': `${logoSVG.skippy.base}--gray`,
  'compassion-experience--white': `${logoSVG['compassion-experience'].base}--white`,
  'compassion-experience--full': `${logoSVG['compassion-experience'].base}--full`,
  'compassion-experience--gray': `${logoSVG['compassion-experience'].base}--gray`,
  'compassion-explorer--white': `${logoSVG['compassion-explorer'].base}--white`,
  'compassion-explorer--full': `${logoSVG['compassion-explorer'].base}--full`,
  'compassion-explorer--gray': `${logoSVG['compassion-explorer'].base}--gray`,
  'compassion-moments--white': `${logoSVG['compassion-moments'].base}--white`,
  'compassion-moments--gray': `${logoSVG['compassion-moments'].base}--gray`,
  'compassion-journey--white': `${logoSVG['compassion-journey'].base}--white`,
  'compassion-journey--gray': `${logoSVG['compassion-journey'].base}--gray`,
  'compassion-sunday--white': `${logoSVG['compassion-sunday'].base}--white`,
  'compassion-sunday--gray': `${logoSVG['compassion-sunday'].base}--gray`,
  'apple-pay--white': `${logoSVG['apple-pay'].base}--white`,
};

const getLogoData = (name: LogoName, color?: LogoColors) => {
  if (!color) {
    return logoSVG[name];
  }
  const colorName = logoColors[color];
  const keyWithColor = `${name}--${colorName}` as keyof typeof logoSVGColor;

  return {
    base: logoSVGColor[keyWithColor] || logoSVG[name].base,
    width: logoSVG[name].width,
    height: logoSVG[name].height,
  };
};

export const LogoChooser = ({
  name,
  alt,
  width,
  height,
  color,
  priority = false,
  ...props
}: LogoChooserProps) => {
  const logoData = getLogoData(name, color);

  let logoWidth: number;
  let logoHeight: number;

  // Calculate correct dimensions based on what's provided
  if (width && !height) {
    logoWidth = width as number;
    logoHeight = (logoData.height / logoData.width) * logoWidth;
  } else if (height && !width) {
    logoHeight = height as number;
    logoWidth = (logoData.width / logoData.height) * logoHeight;
  } else {
    // Both specified: use both
    logoWidth = (width as number) ?? logoData.width;
    logoHeight = (height as number) ?? logoData.height;
  }

  return (
    <Image
      {...props}
      className={cn('LogoChooser LogoChooser__svg', props.className)}
      src={`/logos/${logoData.base}.svg`}
      alt={alt ?? name}
      width={logoWidth}
      height={logoHeight}
      priority={priority}
    />
  );
};
